import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './Services.css';

function Services() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'Services | Sapium Beauty - Skin & Laser Treatments East London';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Explore Sapium Beauty services in East London (Canning Town): advanced facials, chemical peels, microneedling, dermaplaning, mesotherapy, polynucleotides, skin boosters and more at 28 Barking Rd, London E16 1EQ.'
      );
    }
  }, []);

  const services = [
    {
      id: 'facial',
      title: t('services.treatments.facial.title'),
      description: t('services.treatments.facial.description'),
      benefits: t('services.treatments.facial.benefits', { returnObjects: true }),
      duration: t('services.durations.facial'),
      price: t('services.prices.facial'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'chemical-peels',
      title: t('services.treatments.chemical-peels.title'),
      description: t('services.treatments.chemical-peels.description'),
      benefits: t('services.treatments.chemical-peels.benefits', { returnObjects: true }),
      subsections: [
        {
          title: 'BioRePeel',
          description:
            'A no-downtime peel designed to brighten, refine texture and support smoother, clearer-looking skin. Ideal when you want visible glow and skin renewal with minimal peeling.'
        },
        {
          title: 'PRX-T33',
          description:
            'A biorevitalising peel that supports firmness and skin quality while improving tone and texture. Designed for visible rejuvenation with minimal downtime.'
        }
      ],
      duration: t('services.durations.chemical-peels'),
      price: t('services.prices.chemical-peels'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'acne-treatment',
      title: 'Acne Treatment',
      description:
        'Targeted treatments designed to calm inflammation, reduce congestion and support clearer-looking skin. We tailor the approach to your skin type and acne stage, focusing on improving breakouts while supporting the skin barrier.',
      benefits: [
        'Helps reduce active breakouts and congestion',
        'Calms redness and inflammation',
        'Supports a healthier skin barrier',
        'Helps improve uneven texture and post-blemish marks',
        'Personalised plan based on your skin goals'
      ],
      duration: '45-60 minutes',
      price: 'From £85',
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'rosacea-couperose-treatments',
      title: 'Rosacea & Couperose Treatments',
      description:
        'Gentle, skin-barrier focused treatments designed for sensitive skin prone to redness. Our rosacea and couperose support sessions aim to calm the skin, reduce visible flushing and improve comfort without aggressive exfoliation.',
      benefits: [
        'Soothes sensitivity and reduces visible redness',
        'Supports the skin barrier and improves comfort',
        'Helps calm flushing and reactivity',
        'Uses gentle, non-aggressive protocols',
        'Tailored care for sensitive, redness-prone skin'
      ],
      duration: '45-60 minutes',
      price: 'From £85',
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'microneedling',
      title: t('services.treatments.microneedling.title'),
      description: t('services.treatments.microneedling.description'),
      benefits: t('services.treatments.microneedling.benefits', { returnObjects: true }),
      duration: t('services.durations.microneedling'),
      price: t('services.prices.microneedling'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'dermaplaning',
      title: t('services.treatments.dermaplaning.title'),
      description: t('services.treatments.dermaplaning.description'),
      benefits: t('services.treatments.dermaplaning.benefits', { returnObjects: true }),
      duration: t('services.durations.dermaplaning'),
      price: t('services.prices.dermaplaning'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'mesotherapy',
      title: t('services.treatments.mesotherapy.title'),
      description: t('services.treatments.mesotherapy.description'),
      benefits: t('services.treatments.mesotherapy.benefits', { returnObjects: true }),
      subsections: [
        {
          title: 'Dermaheal HSR',
          description:
            'A revitalising mesotherapy cocktail designed to support hydration, improve skin tone and boost overall radiance—ideal for dullness and early signs of ageing.'
        },
        {
          title: 'Fillmed NCTF 135HA',
          description:
            'A well-known skin revitalisation formula combining hyaluronic acid with vitamins, minerals and amino acids to help improve glow, texture and hydration over a course.'
        }
      ],
      duration: t('services.durations.mesotherapy'),
      price: t('services.prices.mesotherapy'),
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'polynucleotides',
      title: 'Polynucleotides (PDRN) Skin Rejuvenation',
      description: 'A regenerative treatment that supports skin repair, hydration and elasticity. Polynucleotides (often known as PDRN) work by stimulating the skin’s natural renewal processes, helping improve texture, glow and overall skin quality over a course of sessions.',
      benefits: [
        'Improves skin hydration and radiance',
        'Supports collagen and elastin production',
        'Helps soften fine lines and improve texture',
        'Strengthens the skin barrier',
        'Great for tired, dull or dehydrated skin',
        'Treatment areas: Face',
        'Treatment areas: Under-eye area'
      ],
      subsections: [
        {
          title: 'VITARAN',
          description:
            'A polynucleotide skin rejuvenation option focused on supporting repair, hydration and improved texture—ideal for tired, dull or compromised skin.'
        },
        {
          title: 'PLINEST',
          description:
            'A bio-revitalising polynucleotide option designed to support skin quality, elasticity and glow over a course of treatments.'
        },
        {
          title: 'Rejuran',
          description:
            'A regenerative polynucleotide/PDRN treatment that supports barrier repair and smoother-looking texture—great for sensitised or dehydrated skin.'
        },
        {
          title: 'PhilArt',
          description:
            'A polynucleotide-based revitalisation option aimed at improving overall skin quality, radiance and firmness with minimal downtime.'
        }
      ],
      duration: '45-60 minutes',
      price: 'From £180',
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'skin-boosters',
      title: 'Skin Boosters',
      description: 'A deep hydration and skin quality treatment designed to improve plumpness, smoothness and glow. Skin boosters help restore moisture levels and support long-term improvement in skin texture, especially for dryness and early signs of ageing.',
      benefits: [
        'Deep, long-lasting hydration',
        'Improves skin firmness and elasticity',
        'Refines texture and enhances glow',
        'Helps reduce fine lines caused by dehydration',
        'Ideal for face, neck and décolletage'
      ],
      subsections: [
        {
          title: 'Seventy Hyal 2000',
          description:
            'A hyaluronic-acid skin booster designed to deeply hydrate, improve glow and support smoother-looking skin. Ideal for dehydration, dullness and fine lines caused by dryness.'
        },
        {
          title: 'Jalupro Classic Skin Booster',
          description:
            'A bio-revitalising booster combining hyaluronic acid with amino acids to support skin repair and elasticity. Great for tired-looking skin, uneven texture and early signs of ageing.'
        },
        {
          title: 'Profhilo',
          description:
            'A high-purity hyaluronic acid treatment that improves hydration and skin quality while supporting firmness. Best for improving overall texture, laxity and radiance over a course.'
        },
        {
          title: 'Rejuran Healer',
          description:
            'A regenerative skin booster (often known as polynucleotides/PDRN) focused on skin repair, barrier support and improved texture. Ideal for compromised, sensitised or dull skin.'
        }
      ],
      duration: '45-60 minutes',
      price: 'From £180',
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'hair-treatment',
      title: 'Hair & Scalp Treatment',
      description: 'A scalp-focused treatment designed to support healthier hair growth and improve scalp condition. Tailored to concerns such as shedding, thinning or scalp imbalance, using targeted actives and techniques to nourish the scalp and strengthen hair.',
      benefits: [
        'Supports scalp health and microcirculation',
        'Helps reduce shedding and improves hair strength',
        'Targets dryness, irritation or imbalance',
        'Nourishes follicles with targeted actives',
        'Suitable for women and men'
      ],
      subsections: [
        {
          title: 'Dr CYJ Hair Filler (Injectable)',
          description:
            'An injectable scalp treatment designed to support healthier hair growth and reduce excessive shedding by delivering targeted active ingredients to the scalp over a course of sessions.'
        },
        {
          title: 'Plinest Hair',
          description:
            'A bio-revitalising injectable treatment that supports scalp health and hair quality, helping improve hydration, resilience and follicle support for thinning or weakened hair.'
        },
        {
          title: 'Scalp Microneedling',
          description:
            'A scalp treatment using fine microneedles to stimulate microcirculation and support healthier-looking hair growth. It can also enhance absorption of targeted scalp serums, and works best as a course.'
        }
      ],
      duration: '45-60 minutes',
      price: 'From £180',
      location: t('services.locations.mainTreatments')
    },
    {
      id: 'microneedling-advanced-skin-boosters',
      title: 'Microneedling with Advanced Skin Boosters',
      description: 'A results-driven combination of microneedling with advanced skin boosters to enhance collagen induction, improve skin texture and maximise glow. This treatment is ideal for uneven tone, enlarged pores, fine lines and post-acne marks. A booster is selected based on your skin goals.',
      benefits: [
        'Boosts collagen for firmer, smoother skin',
        'Improves texture, pores and overall tone',
        'Enhances radiance and hydration',
        'Supports improvement of post-acne marks',
        'Personalised booster selection for your skin goals'
      ],
      subsections: [
        {
          title: 'Microneedling + Hyaluronic Acid',
          description:
            'Focused on deep hydration, plumpness and glow. Ideal for dehydrated skin, dullness and fine lines caused by dryness.'
        },
        {
          title: 'Microneedling + Salmon DNA',
          description:
            'A regenerative booster option designed to support skin renewal, improve texture and enhance radiance over a course of treatments.'
        },
        {
          title: 'Microneedling + Exosomes',
          description:
            'An advanced option aimed at supporting skin recovery and visible revitalisation, helping improve overall skin quality and resilience.'
        },
        {
          title: 'Microneedling + BioRePeel',
          description:
            'Combines collagen induction with a revitalising peel to enhance brightness, refine texture and support a clearer-looking complexion.'
        },
        {
          title: 'Microneedling + PRX-T33',
          description:
            'A no-downtime biorevitalising peel combined with microneedling to support firmness, smoothness and a more even-looking skin tone.'
        }
      ],
      duration: '60-90 minutes',
      price: 'From £220',
      location: t('services.locations.mainTreatments')
    }
  ];

  return (
    <div className="services-page">
      <section className="services-hero">
        <div className="container">
          <h1>{t('services.hero.title')}</h1>
          <p>{t('services.hero.description')}</p>
        </div>
      </section>

      <section className="services-grid">
        <div className="container">
          {services.map((service) => (
            <div key={service.id} className="service-card">
              <h2>{service.title}</h2>
              <p className="service-description">{service.description}</p>
              
              <div className="service-details">
                <div className="benefits">
                  <h3>{t('services.labels.benefits')}</h3>
                  <ul className={service.id === 'microneedling-advanced-skin-boosters' ? 'benefits-list two-columns' : 'benefits-list'}>
                    {(Array.isArray(service.benefits) ? service.benefits : []).map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {Array.isArray(service.subsections) && service.subsections.length > 0 && (
                <div
                  className={`service-subsections ${
                    service.id === 'microneedling-advanced-skin-boosters' ? 'two-columns' : ''
                  }`}
                  aria-label="Service subsections"
                >
                  {service.subsections.map((item) => (
                    <div key={item.title} className="service-subsection">
                      <h3 className="service-subsection-title">{item.title}</h3>
                      <p className="service-subsection-description">{item.description}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {service.id === 'chemical-peels' && (
                <Link to="/services/chemical-peels" className="service-detail-button">
                  Learn More
                </Link>
              )}
              
              {service.id === 'microneedling' && (
                <Link to="/services/microneedling" className="service-detail-button">
                  Learn More
                </Link>
              )}
              
              {service.id === 'microneedling-biorepeel' && (
                <Link to="/services/microneedling-biorepeel" className="service-detail-button">
                  Learn More
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="services-grid">
        <div className="container">
          <div className="service-card" style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', border: '2px solid #d4af37' }}>
            <h2>{t('services.locations.laserHairRemovalTitle')}</h2>
            <p className="service-description">
              {t('services.locations.laserHairRemovalDescription')}
            </p>
            <div className="service-details">
              <div className="benefits" style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ color: '#d4af37', marginBottom: '0.75rem' }}>{t('services.locations.technologyTitle')}</h3>
                <p style={{ fontSize: '1rem', lineHeight: '1.6', color: '#555' }}>
                  {t('services.locations.laserHairRemovalTechnology')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>{t('services.cta.title')}</h2>
          <p>{t('services.cta.description')}</p>
          <a href="https://www.fresha.com/book-now/sapium-beauty-hu8f8ild/all-offer?share=true&pId=2743251" target="_blank" rel="noopener noreferrer" className="cta-button">{t('services.cta.button')}</a>
        </div>
      </section>
    </div>
  );
}

export default Services; 